/**
 * Environment Configuration
 * Centralized access to environment variables with type safety
 *
 * Environment files are loaded by Vite in this order:
 *   1. .env                  - Base config (always loaded)
 *   2. .env.local            - Local overrides (ignored by git)
 *   3. .env.[mode]           - Mode-specific (.env.development or .env.production)
 *   4. .env.[mode].local     - Mode-specific local overrides (ignored by git)
 */

/**
 * Get the API URL based on environment
 *
 * Priority:
 *   1. VITE_API_URL from environment files (set in .env.development / .env.production)
 *   2. Fallback to production URL if in production mode
 *   3. Fallback to localhost for development
 */
function getApiUrl(): string {
  // Use environment variable if explicitly set
  if (import.meta.env.VITE_API_URL) {
    return import.meta.env.VITE_API_URL;
  }

  // Fallback based on mode (safety net if env files are misconfigured)
  if (import.meta.env.PROD) {
    console.warn('[env] VITE_API_URL not set, falling back to production URL');
    return 'https://insights-service.eclou.digital';
  }

  console.warn('[env] VITE_API_URL not set, falling back to localhost');
  return 'http://localhost:8080';
}

/**
 * Environment configuration object
 */
export const env = {
  /**
   * API base URL
   * - Development: '' (proxied via Vite to http://localhost:8080)
   * - Production: https://insights-service.eclou.digital
   */
  apiUrl: getApiUrl(),

  /**
   * Current environment mode
   */
  mode: import.meta.env.MODE,

  /**
   * Whether running in development mode
   */
  isDev: import.meta.env.DEV,

  /**
   * Whether running in production mode
   */
  isProd: import.meta.env.PROD,

  /**
   * Whether running in test mode
   */
  isTest: import.meta.env.MODE === 'test',

  /**
   * Analysis request configuration
   */
  analysis: {
    /** Product ID mapping by Zielgruppe */
    productIdByZielgruppe: {
      privatkunden: Number(import.meta.env.VITE_PRODUCT_ID_PRIVATKUNDEN) || 1,
      geschaeftskunden: Number(import.meta.env.VITE_PRODUCT_ID_GESCHAEFTSKUNDEN) || 2,
    },
    /** Get product ID for a given Zielgruppe */
    getProductId(zielgruppe: string): number {
      if (zielgruppe === 'privatkunden') {
        return this.productIdByZielgruppe.privatkunden;
      }
      if (zielgruppe === 'geschaeftskunden') {
        return this.productIdByZielgruppe.geschaeftskunden;
      }
      // Default to privatkunden for custom/andere values
      return this.productIdByZielgruppe.privatkunden;
    },
    /** Parameter names mapping */
    parameterNames: {
      vertriebsgebiet: import.meta.env.VITE_PARAM_VERTRIEBSGEBIET || 'vertriebsgebiet',
      zielgruppe: import.meta.env.VITE_PARAM_ZIELGRUPPE || 'zielgruppe',
      branche: import.meta.env.VITE_PARAM_BRANCHE || 'branche',
      vertriebsziel: import.meta.env.VITE_PARAM_VERTRIEBSZIEL || 'vertriebsziel',
      produktlink: import.meta.env.VITE_PARAM_PRODUKTLINK || 'produktlink',
      vertriebszielDetails: import.meta.env.VITE_PARAM_VERTRIEBSZIEL_DETAILS || 'vertriebsziel_details',
    },
    /** Feature names returned by API (used for card matching) */
    featureNames: {
      vertriebsgebiet: import.meta.env.VITE_FEATURE_VERTRIEBSGEBIET || 'vertriebsgebiet',
      zielgruppe: import.meta.env.VITE_FEATURE_ZIELGRUPPE || 'zielgruppe',
      produkt: import.meta.env.VITE_FEATURE_PRODUKT || 'product',
      kommunikation: import.meta.env.VITE_FEATURE_KOMMUNIKATION || 'kommunikation',
      massnahmen: import.meta.env.VITE_FEATURE_MASSNAHMEN || 'massnahmen',
      handlungsempfehlungen: import.meta.env.VITE_FEATURE_HANDLUNGSEMPFEHLUNGEN || 'handlungsempfehlungen',
      swot: import.meta.env.VITE_FEATURE_SWOT || 'swot',
      zielgruppeB2B: import.meta.env.VITE_FEATURE_ZIELGRUPPE_B2B || 'zielgruppe_b2b',
    },
    /** Analysis variant detection based on productId */
    analysisVariant: {
      /** Product IDs that use V1 (standard) analysis layout (comma-separated) */
      v1ProductIds: (import.meta.env.VITE_ANALYSIS_V1_PRODUCT_IDS || '1').split(',').map((id: string) => parseInt(id.trim(), 10)),
      /** Product IDs that use V2 analysis layout (comma-separated) */
      v2ProductIds: (import.meta.env.VITE_ANALYSIS_V2_PRODUCT_IDS || '2').split(',').map((id: string) => parseInt(id.trim(), 10)),
    },
  },
} as const;

/**
 * Type for the environment object
 */
export type Env = typeof env;
